import { useSortable } from '@dnd-kit/sortable';
import type { RowData, Row } from '@tanstack/react-table';
import { Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import {
  useCallback,
  useRef,
  useState,
  type ComponentProps,
  type ReactElement,
  type JSX,
} from 'react';

import {
  DATA_TABLE_DETAILS_ROW_IDENTIFIER,
  DATA_TABLE_DETAILS_ROW_PARITY,
  DATA_TABLE_ROW_EXPANDED_ID_SUFFIX,
} from '../constants.js';
import { useDataTable } from '../contexts/DataTableContext.js';
import { RowDetailsElement } from '../features/RowDetails/components/RowDetailsElement.js';
import { RowOrderingDragAndDropContext } from '../features/RowOrder/components/RowOrderingDragAndDropContext.js';
import type { DataTableRowOrderState } from '../features/RowOrder/row-order-types.js';
import type { TDataTableFeatures } from '../features/types/features.types.js';
import * as styles from './DataTableBody.sty.js';
import { DataTableRow } from './DataTableRow.js';

interface DraggedRowSubscribeProps {
  rowId: string;
  children: (isDraggedRow: boolean) => JSX.Element;
}

/** Wraps table.Subscribe with a stable selector per row, avoiding new closure allocations on every DataTableBody render. */
function DraggedRowSubscribe({
  rowId,
  children,
}: Readonly<DraggedRowSubscribeProps>): JSX.Element {
  const table = useDataTable();
  const selector = useCallback(
    (
      draggingRowInfo:
        | DataTableRowOrderState<TDataTableFeatures>['draggingRowInfo']
        | undefined,
    ): boolean => {
      if (!draggingRowInfo) {
        return false;
      }
      const draggingRowId = draggingRowInfo.id;
      return (
        rowId === draggingRowId ||
        rowId === `${draggingRowId}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`
      );
    },
    [rowId],
  );

  return (
    <table.Subscribe source={table.atoms.draggingRowInfo} selector={selector}>
      {children}
    </table.Subscribe>
  );
}

/** Wraps DataTableRow with useSortable, only rendered when row reordering is enabled. */
function SortableDataTableRow(
  props: Readonly<ComponentProps<typeof DataTableRow>>,
): JSX.Element {
  const { setNodeRef } = useSortable({
    id: props.row.id,
    data: {
      row: props.row,
      rowIndex: props.virtualItemIndex,
      virtualRow: props.virtualRow,
    },
  });
  return <DataTableRow {...props} dragAndDropRef={setNodeRef} />;
}

export function DataTableBody(
  props: Readonly<{
    rows: Row<TDataTableFeatures, RowData>[];
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
    columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  }>,
): ReactElement | null {
  const { rows, rowVirtualizer, columnVirtualizer } = props;
  const table = useDataTable();

  // Keep a list of all rows that are currently measured by the virtualizer
  const queuedMeasurementRowIdRef = useRef(new Set());

  const rowMeasure = useCallback(
    (node: Element | null): void => {
      if (!node || !(node instanceof HTMLElement)) {
        return;
      }
      const rowId = node.getAttribute('data-rowid');
      if (rowId === null || queuedMeasurementRowIdRef.current.has(rowId)) {
        return;
      }
      queuedMeasurementRowIdRef.current.add(rowId);
      requestAnimationFrame(() => {
        // Ensure the virtualizer only measures element that are still connected to the DOM. Detached elements will be ignored.
        if (node.isConnected) {
          rowVirtualizer.measureElement(node);
        }
        // Remove the row from the queued measurement list, so it can be measured again in the future.
        queuedMeasurementRowIdRef.current.delete(rowId);
      });
    },
    [rowVirtualizer],
  );

  const columnMeasure = useCallback(
    (node: Element | null) => {
      if (!node || !(node instanceof HTMLElement)) {
        return;
      }
      columnVirtualizer.measureElement(node);
    },
    [columnVirtualizer],
  );

  const RowComponent = table.options.enableRowOrderDragAndDrop
    ? SortableDataTableRow
    : DataTableRow;

  const firstRenderedRowIndex = rows.at(0)?.index;

  /*
   * `getVirtualItems()` returns a new array whenever a column is remeasured,
   * even when the same columns stay rendered. Rows only read `index` and
   * `key`, so tying the array identity to the rendered window instead lets
   * memoized rows skip those re-renders.
   */
  const virtualColumnIndexes = columnVirtualizer.getVirtualIndexes();
  const [prevVirtualColumnIndexes, setPrevVirtualColumnIndexes] =
    useState(virtualColumnIndexes);
  const [virtualColumns, setVirtualColumns] = useState(() =>
    columnVirtualizer.getVirtualItems(),
  );
  if (prevVirtualColumnIndexes !== virtualColumnIndexes) {
    setPrevVirtualColumnIndexes(virtualColumnIndexes);
    setVirtualColumns(columnVirtualizer.getVirtualItems());
  }

  const rowElements: JSX.Element[] = rowVirtualizer
    .getVirtualItems()
    .map((virtualRow, index) => {
      const originalRow = rows[virtualRow.index];
      let parity: 'even' | 'odd' = virtualRow.index % 2 === 0 ? 'even' : 'odd';

      // If the row parity is set in the row, use this one as it
      // could change via row details expansion.
      const detailsParity = originalRow[DATA_TABLE_DETAILS_ROW_PARITY];
      if (detailsParity !== undefined) {
        parity = detailsParity;
      }

      // Need to run this through the symbol accessor instead of the
      // feature, as the feature does not get the information from the
      // extended row model, as the row model runs later.
      if (originalRow[DATA_TABLE_DETAILS_ROW_IDENTIFIER] === true) {
        return (
          <DraggedRowSubscribe key={originalRow.id} rowId={originalRow.id}>
            {(isDraggedRow) => (
              <RowDetailsElement
                originalRow={originalRow}
                virtualRow={virtualRow}
                virtualItemIndex={index}
                rowSeparation={
                  table.atoms.visualVariantConfig?.get()?.rowSeparation
                }
                rowParity={parity}
                isLastRowOnPage={virtualRow.index === rows.length - 1}
                rowMeasure={rowMeasure}
                isDraggedRow={isDraggedRow}
              >
                {table.options.rowDetailsTemplate?.({
                  row: originalRow.original,
                })}
              </RowDetailsElement>
            )}
          </DraggedRowSubscribe>
        );
      }

      return (
        <DraggedRowSubscribe
          key={`row-${virtualRow.key}`}
          rowId={originalRow.id}
        >
          {(isDraggedRow) => (
            <RowComponent
              row={originalRow}
              virtualRow={virtualRow}
              virtualItemIndex={index}
              rowCount={rows.length}
              virtualColumns={virtualColumns}
              isDraggedRow={isDraggedRow}
              isInDragOverlay={false}
              rowParity={parity}
              rowMeasure={rowMeasure}
              columnMeasure={
                // For columns, only measure the first rendered row.
                // All other rows will have the same column widths.
                firstRenderedRowIndex === virtualRow.index
                  ? columnMeasure
                  : undefined
              }
              scrollToIndex={rowVirtualizer.scrollToIndex}
            />
          )}
        </DraggedRowSubscribe>
      );
    });

  const bodyContent = table.options.enableRowOrderDragAndDrop ? (
    <RowOrderingDragAndDropContext
      rows={rows}
      virtualColumns={virtualColumns}
      rowSeparation={table.atoms.visualVariantConfig?.get()?.rowSeparation}
    >
      {rowElements}
    </RowOrderingDragAndDropContext>
  ) : (
    // If row order drag and drop is not enabled then don't use the RowOrderingDragAndDropContext
    // oxlint-disable-next-line react/jsx-no-useless-fragment
    <>{rowElements}</>
  );

  return (
    <div
      role="rowgroup"
      data-rowgroup="body"
      className={clsx(styles.body, {
        [styles.loading]: table.atoms.loading?.get(),
      })}
    >
      {bodyContent}
    </div>
  );
}

DataTableBody.displayName = 'DataTableBody';
